// Features: M004-F0807 (WebUI #270, milestone M2b: the read-only "Review update" view)
// Tests: T-M004-0467 (see CalendarUpdateReview.test.js beside this file).
//
// Read-only view. The Accept and link controls are mounted below the changes (CalendarUpdateAcceptPanel), behind CALENDAR_UPDATE_ACCEPT_ENABLED.
// Renders nothing when the view gate is off or the user lacks ledger_manage, and makes no request in that case.
import React, { useEffect, useState } from 'react';
import CalendarUpdateAcceptPanel from './CalendarUpdateAcceptPanel';
import {
  CALENDAR_UPDATE_ACCEPT_ENABLED, CALENDAR_UPDATE_VIEW_ENABLED, formatChanges, itemStateText, replacementLinkText, refusalSentence,
  correctionReviewStatus, correctionReviewText,
} from './calendarUpdateModel';

const NOT_FOUND = refusalSentence({ response: { data: { error: 'PROPOSAL_NOT_FOUND' } } });

const actorName = (actor) => (actor ? (actor.display_name || (Number.isInteger(actor.user_id) ? `user ${actor.user_id}` : '')) : '');

export default function CalendarUpdateReview({
  proposal: proposalProp = null,
  proposalId,
  orgId,
  canManage,
  viewEnabled = CALENDAR_UPDATE_VIEW_ENABLED,
  acceptEnabled = CALENDAR_UPDATE_ACCEPT_ENABLED,
  api,
  onClose,
  onDecided,
}) {
  const [loaded, setLoaded] = useState(null);
  const [error, setError] = useState('');
  const [review, setReview] = useState(null);
  const [reviewError, setReviewError] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);

  const active = Boolean(viewEnabled && canManage);
  const needsFetch = active && !proposalProp && Boolean(proposalId);

  useEffect(() => {
    if (!needsFetch) return undefined;
    let cancelled = false;
    setError('');
    api.list(orgId, { proposal_id: proposalId, state: 'any' })
      .then((answer) => {
        if (cancelled) return;
        const found = (answer?.data?.data?.proposals || []).find((row) => row.proposal_id === proposalId);
        if (found) setLoaded(found);
        else setError(NOT_FOUND);
      })
      .catch((failure) => { if (!cancelled) setError(refusalSentence(failure)); });
    return () => { cancelled = true; };
  }, [needsFetch, api, orgId, proposalId]);

  if (!active) return null;

  const proposal = proposalProp || loaded;
  if (!proposal) {
    return error ? <p role="alert" className="calendar-update-review__error">{error}</p> : null;
  }

  const changes = formatChanges(proposal);
  const decided = proposal.state !== 'pending';
  const itemText = itemStateText(proposal.item_state);
  const linkText = replacementLinkText(proposal.replacement_link);
  const handle = proposal.correction_review;
  const showReviewButton = proposal.item_state === 'written' && Boolean(handle);

  const loadReview = async () => {
    setReviewBusy(true);
    setReviewError('');
    try {
      const answer = await api.correctionReviewGet(orgId, handle.review_id);
      setReview(answer?.data?.data?.review || null);
    } catch (failure) {
      setReviewError(refusalSentence(failure));
    } finally {
      setReviewBusy(false);
    }
  };

  const reviewStatus = review ? correctionReviewStatus(review) : null;
  const reviewText = reviewStatus ? correctionReviewText(reviewStatus, review.documents) : null;

  return (
    <section className="calendar-update-review" aria-label="Review update">
      <header>
        <h2>Review update</h2>
        {proposal.booking_label && <p className="calendar-update-review__booking">{proposal.booking_label}</p>}
        {proposal.customer_display_name && <p>{proposal.customer_display_name}</p>}
        <p>Occurrence {proposal.occurrence_date}</p>
        <p>State: <span className="calendar-update-review__state">{proposal.state}</span></p>
        {decided && proposal.decided_at && (
          <p className="calendar-update-review__decided">
            Decided {proposal.decided_at}{actorName(proposal.actor) ? ` by ${actorName(proposal.actor)}` : ''}
          </p>
        )}
      </header>

      {error && <p role="alert" className="calendar-update-review__error">{error}</p>}

      <ol className="calendar-update-review__changes">
        {changes.map((change, index) => (
          <li key={`${change.kind}-${index}`} className="calendar-update-review__change">
            <span className="calendar-update-review__label">{change.label}</span>
            <span className="calendar-update-review__current">
              <span className="calendar-update-review__hint">In Ledger: </span>
              <span data-role="current">{change.current}</span>
            </span>
            <span className="calendar-update-review__proposed">
              <span className="calendar-update-review__hint">Proposed by Core: </span>
              <span data-role="proposed">{change.proposed}</span>
            </span>
          </li>
        ))}
      </ol>

      {itemText && <p className="calendar-update-review__item-state">{itemText}</p>}
      {linkText && <p className="calendar-update-review__replacement">{linkText}</p>}

      <CalendarUpdateAcceptPanel
        proposal={proposal}
        canManage={canManage}
        acceptEnabled={acceptEnabled}
        viewEnabled={viewEnabled}
        api={api}
        orgId={orgId}
        onDecided={onDecided}
      />

      {showReviewButton && (
        <div className="calendar-update-review__correction">
          <button type="button" onClick={loadReview} disabled={reviewBusy}>Correction review</button>
          {reviewText && <p className="calendar-update-review__correction-status">{reviewText}</p>}
          {reviewError && <p role="alert" className="calendar-update-review__error">{reviewError}</p>}
        </div>
      )}

      {onClose && <button type="button" onClick={onClose}>Close</button>}
    </section>
  );
}
